import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../utils/api';
import { Search } from 'lucide-react';

const SearchIc = () => <Search size={16} />;

/**
 * Command-palette style global search. Opens with Ctrl+K / Cmd+K from
 * anywhere, or by clicking the search bar wherever it's mounted (e.g. Layout).
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open,    setOpen]    = useState(false);
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const runSearch = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      try { const r = await searchAPI.query(q); setResults(r.data); }
      catch(e) { setResults([]); }
    }, 250);
  }, []);

  const handleChange = (e) => { setQuery(e.target.value); runSearch(e.target.value); };

  const goTo = (path) => { setOpen(false); setQuery(''); setResults(null); navigate(path); };

  const sections = results ? [
    { label:'Projects', items: results.projects, render: p => ({ title:p.name, sub:p.status, path:`/projects/${p.id}` }) },
    { label:'Tasks',    items: results.tasks,    render: t => ({ title:t.title, sub:t.status, path:`/tasks/${t.id}` }) },
    { label:'Bugs',     items: results.bugs,     render: b => ({ title:b.title, sub:b.severity, path:`/bugs/${b.id}` }) },
    { label:'People',   items: results.users,    render: u => ({ title:u.name, sub:u.role, path:`/users` }) },
  ].filter(s => s.items?.length > 0) : [];

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors w-full max-w-xs"
        style={{ color:'rgba(255,255,255,0.6)', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)' }}
        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.14)'}
        onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
        <SearchIc/> <span className="flex-1 text-left">Search...</span>
        <span className="px-1.5 py-0.5 rounded text-xs" style={{ background:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.5)' }}>⌘K</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)}/>
          <div className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden z-10"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor:'var(--border)' }}>
              <SearchIc/>
              <input ref={inputRef} value={query} onChange={handleChange}
                placeholder="Search projects, tasks, bugs, people..."
                className="flex-1 outline-none bg-transparent text-sm" style={{ color:'var(--text)' }}/>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {!results && query.length >= 2 && (
                <p className="text-sm text-center py-8" style={{ color:'var(--text-secondary)' }}>Searching...</p>
              )}
              {results && sections.length === 0 && (
                <p className="text-sm text-center py-8" style={{ color:'var(--text-secondary)' }}>No results for "{query}"</p>
              )}
              {sections.map(section => (
                <div key={section.label} className="py-1">
                  <p className="text-xs font-bold uppercase tracking-wider px-4 py-1.5" style={{ color:'var(--text-secondary)' }}>{section.label}</p>
                  {section.items.map((item, i) => {
                    const r = section.render(item);
                    return (
                      <button key={i} onClick={() => goTo(r.path)}
                        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[var(--surface-alt)] transition-colors">
                        <span className="text-sm truncate" style={{ color:'var(--text)' }}>{r.title}</span>
                        <span className="text-xs flex-shrink-0 ml-2" style={{ color:'var(--text-secondary)' }}>{r.sub}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
